import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Spin } from 'antd-v5';
import InquiryHeader from './components/InquiryHeader';
import StorySection from './components/StorySection';
import TagsSection from './components/TagsSection';
import RequestedProducts from './components/RequestedProducts';
import ClosedInquiries from './components/ClosedInquiries';
import AddRolexInquiryModal from '../../../../../componentsV2/Rolex/AddRolexInquiryModal';
import { getInquiriesByClient } from '../../../../../api/vendors';
import { getMerchantTags } from '../../../../../services/clients';
import { formatCreatedDate, formatInquiryDate } from '../../../Rolex/utils';
import './styles.scss';

const ROLEX_VENDOR_ID = 686;

const Rolex = ({ client, auth, onShareProducts }) => {
  const [activeInquiry, setActiveInquiry] = useState(null);
  const [closedInquiries, setClosedInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [tagLookup, setTagLookup] = useState([]);

  const clientId = client?.client_id || client?.id;

  useEffect(() => {
    getMerchantTags({ query: { limit: 500 }, loadingStatusOff: true })
      .then(response => setTagLookup(response?.results || []))
      .catch(() => {});
  }, []);

  const fetchInquiries = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const response = await getInquiriesByClient(clientId);
      const active = response?.data || [];
      const closed = response?.closedInquiries || [];

      if (active.length > 0) {
        const inq = active[0];
        let resolvedTags;
        if (inq.tags && inq.tags.length > 0 && typeof inq.tags[0] === 'object') {
          resolvedTags = inq.tags
            .map(t => ({
              name: t.tagName || t.name,
              color: t.color || tagLookup.find(mt => mt.id === t.tagId)?.color || '#818181',
            }))
            .filter(t => t.name);
        } else {
          resolvedTags = (inq.tagIds || [])
            .map(id => {
              const lookup = tagLookup.find(t => t.id === id);
              return lookup ? { name: lookup.name, color: lookup.color || '#818181' } : null;
            })
            .filter(Boolean);
        }
        setActiveInquiry({
          ...inq,
          createdDate: formatCreatedDate(inq.createdDt),
          createdByName: inq.createdByName || '',
          tags: resolvedTags,
          requestedProducts: inq.requestedProducts || [],
        });
      } else {
        setActiveInquiry(null);
      }

      setClosedInquiries(
        closed.map(inq => ({
          ...inq,
          createdDate: formatInquiryDate(inq.createdDt),
          closedDate: formatInquiryDate(inq.lastModifiedTs),
          associateName: inq.associateName || '',
        })),
      );
    } catch {
      setActiveInquiry(null);
      setClosedInquiries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, [clientId, tagLookup]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!activeInquiry && closedInquiries.length === 0) {
    return (
      <div style={{ padding: '26px', color: 'rgba(0,0,0,0.5)', fontSize: 14 }}>No Rolex inquiries for this client</div>
    );
  }

  const editInquiry = activeInquiry
    ? {
        ...activeInquiry,
        clientId,
        clientName: client?.name,
        associate: activeInquiry.associateName,
        models: activeInquiry.requestedProducts?.map(p => p.mpn) || [],
        inquiryDate: activeInquiry.createdDate,
      }
    : null;

  return (
    <div
      className="flex-col-left"
      style={{
        alignItems: 'stretch',
        maxHeight: '100%',
        gap: '36px',
        overflowX: 'hidden',
        paddingLeft: '26px',
        paddingRight: '26px',
      }}
    >
      {activeInquiry && (
        <>
          <InquiryHeader
            createdDate={activeInquiry.createdDate}
            createdBy={activeInquiry.createdByName}
            status={activeInquiry.status}
            onEdit={() => setShowEditModal(true)}
          />
          <StorySection story={activeInquiry.story} />
          <TagsSection tags={activeInquiry.tags} />
          <RequestedProducts products={activeInquiry.requestedProducts} onShare={onShareProducts} />
        </>
      )}
      <ClosedInquiries inquiries={closedInquiries} />

      {editInquiry && (
        <AddRolexInquiryModal
          open={showEditModal}
          onCancel={() => setShowEditModal(false)}
          onSave={() => {
            setShowEditModal(false);
            fetchInquiries();
          }}
          inquiry={editInquiry}
          vendorId={ROLEX_VENDOR_ID}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  client: state.client,
  auth: state.auth,
});

export default connect(mapStateToProps)(Rolex);
